import { useIdsSelect } from '@entities/common/IdsSelector/index.ts';
import { useIdsSelecting } from '@entities/common/IdsSelector/models.ts';
import { useSaveData } from '@entities/common/savedData';
import { createEntityAdapter, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { xhr } from '@shared/config';
import { useAppDispatch, useAppSelector, useUser } from '@shared/hooks';
import type { ContactEditValue, ContactItem } from '@shared/types/contacts';
import { openModalDichotomous } from '@src/library/redux/modal';
import { contactsApi } from './api';

const contactsAdapter = createEntityAdapter({
  selectId: (contact: ContactItem) => contact.id,
});

const contactsSlice = createSlice({
  name: 'coachContacts',
  initialState: contactsAdapter.getInitialState({
    isLoading: false,
    selectedContacts: [] as number[],
  }),
  reducers: {
    setContacts: contactsAdapter.setAll,
    addContact: contactsAdapter.addOne,
    updateContact: contactsAdapter.updateOne,
    removeContact: contactsAdapter.removeOne,
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSelectedContacts: (state, action: PayloadAction<number[]>) => {
      state.selectedContacts = action.payload;
    },
    addSelectedContact: (state, action: PayloadAction<number>) => {
      state.selectedContacts.push(action.payload);
    },
    removeSelectedContact: (state, action: PayloadAction<number>) => {
      state.selectedContacts = state.selectedContacts.filter((id) => id !== action.payload);
    },
  },
});

export const contactsReducer = contactsSlice.reducer;
export const contactsActions = contactsSlice.actions;

const contactsSelectors = contactsAdapter.getSelectors((state: any) => state.coachContacts);

type TempContact = Pick<ContactItem, 'value' | 'type' | 'id'>;

export const useTempContacts = () => {
  const {
    data: tempContacts,
    doSetStorageData,
    doRemoveStorageData,
  } = useSaveData<TempContact[]>('tempContacts');

  const addTempContact = (newContact: Omit<TempContact, 'id'>) => {
    if (tempContacts) {
      const id = tempContacts.length + 1000;
      doSetStorageData([...tempContacts, { ...newContact, id }]);
    } else {
      const id = 1000;
      doSetStorageData([{ ...newContact, id }]);
    }
  };

  const deleteTempContact = (contactId: number) => {
    if (tempContacts) {
      doSetStorageData([...tempContacts.filter((contact) => contact.id === contactId)]);
    }
  };

  const updateTempContact = ({
    contactId,
    data,
  }: {
    contactId: number;
    data: Omit<TempContact, 'id'>;
  }) => {
    if (tempContacts) {
      const contactIndex = tempContacts.findIndex((contact) => contact.id === contactId);
      if (~contactIndex) {
        const newArray = [...tempContacts];
        newArray[contactIndex] = { ...tempContacts[contactIndex], ...data };
        doSetStorageData(newArray);
      }
    }
  };

  return {
    tempContacts: tempContacts || [],
    addTempContact,
    updateTempContact,
    deleteTempContact,
    clearTempContacts: doRemoveStorageData,
  };
};

export const useLoadContacts = () => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.coachContacts.isLoading);
  const { isAuth } = useUser();
  useEffect(() => {
    const fetchContacts = async () => {
      dispatch(contactsActions.setLoading(true));
      try {
        const resp = await xhr.get(contactsApi);
        const contactsList: ContactItem[] = resp.data.results;
        dispatch(contactsActions.setContacts(contactsList));
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(contactsActions.setLoading(false));
      }
    };

    if (isAuth) {
      fetchContacts();
    }
  }, [dispatch]);

  return { isLoading };
};

export const useContact = (id: number) => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.coachContacts.isLoading);
  const contact = useAppSelector((state) => contactsSelectors.selectById(state, id));
  const { isAuth } = useUser();

  useEffect(() => {
    const fetchContact = async () => {
      if (id === 0) return;
      dispatch(contactsActions.setLoading(true));
      try {
        const resp = await xhr.get(`${contactsApi}${id}`);
        const contactData: ContactItem = resp.data;
        dispatch(contactsActions.addContact(contactData));
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(contactsActions.setLoading(false));
      }
    };

    if (isAuth) {
      fetchContact();
    }
  }, [id, dispatch]);

  return { contact, isLoading };
};

export const useContacts = () => {
  const dispatch = useAppDispatch();
  const contacts = useAppSelector(contactsSelectors.selectAll);
  const isLoading = useAppSelector((state) => state.coachContacts.isLoading);
  const selectedContacts = useAppSelector((state) => state.coachContacts.selectedContacts);
  const { addTempContact, deleteTempContact, updateTempContact } = useTempContacts();
  const { isAuth } = useUser();

  const addNewContact = async (contactData: ContactEditValue): Promise<void> => {
    if (isAuth) {
      dispatch(contactsActions.setLoading(true));
      try {
        const resp = await xhr.post(contactsApi, {
          type: contactData.type,
          value: contactData.value,
          description: contactData.description,
        });
        dispatch(contactsActions.addContact(resp.data));
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(contactsActions.setLoading(false));
      }
    } else {
      // для незарегистрированного клиента возможность при регистрации добавить дополнительные контакты
      !isAuth && addTempContact({ type: contactData.type, value: contactData.value });
    }
  };

  const updateCoachContact = async (contactData: ContactItem): Promise<void> => {
    const { id, ...updateData } = contactData;
    if (isAuth) {
      dispatch(contactsActions.setLoading(true));
      try {
        const resp = await xhr.patch(`${contactsApi}${id}/`, updateData);
        dispatch(contactsActions.updateContact({ id, changes: resp.data }));
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(contactsActions.setLoading(false));
      }
    } else {
      // для незарегистрированного клиента возможность при регистрации изменить дополнительные контакты
      !isAuth &&
        updateTempContact({
          contactId: id,
          data: { type: contactData.type, value: contactData.value },
        });
    }
  };

  const deleteContactById = async (contactId: number, onSuccess?: () => void) => {
    if (isAuth) {
      dispatch(
        openModalDichotomous({
          title: 'Удаление контакта',
          text: `Контакт будет удален во всех разделах, где он использовался\n\n Вы действительно хотите удалить его из списка контактов?`,
          confirmText: 'Нет',
          declineText: 'Да',
          confirm: () => {
            dispatch(contactsActions.setLoading(false));
          },
          decline: async () => {
            try {
              dispatch(contactsActions.setLoading(true));
              await xhr.delete(`${contactsApi}${contactId}/`);
              dispatch(contactsActions.removeContact(contactId));
              onSuccess && onSuccess();
            } catch {
            } finally {
              dispatch(contactsActions.setLoading(false));
            }
          },
          onRequestClose: true,
        })
      );
    } else {
      // для незарегистрированного клиента возможность при регистрации удалить дополнительные контакты
      !isAuth && deleteTempContact(contactId);
    }
  };

  const addSelectedContact = (id: number) => {
    dispatch(contactsActions.addSelectedContact(id));
  };

  const removeSelectedContact = (id: number) => {
    dispatch(contactsActions.removeSelectedContact(id));
  };

  return {
    contacts,
    isLoading,
    addNewContact,
    updateCoachContact,
    deleteContactById,
    selectedContacts,
    setSelectedContacts: (ids: number[]) => dispatch(contactsActions.setSelectedContacts(ids)),
    addSelectedContact,
    removeSelectedContact,
  };
};

export const useContactsSelecting = () => {
  const { selectedIds: selectedContactsIds, doResetSelectedIds: resetSelectedContactsIds } =
    useIdsSelect('contacts');

  const { isSelecting, setIsSelectingTrue, setIsSelectingFalse } = useIdsSelecting('contacts');

  return {
    selectedContactsIds: selectedContactsIds || [],
    resetSelectedContactsIds,
    isSelecting,
    setIsSelectingTrue,
    setIsSelectingFalse,
  };
};
